// The ONE owner of auth-session storage (docs/BUSINESS_RULES.md). Every other
// module reads the token through getAuthToken()/getAuthHeader() and clears via
// clearAuthStorage() — the key names are private here on purpose, so a second
// key can never appear. ~55 legacy `localStorage.getItem('token')` literals
// remain across lib/ and pages; migrate them on touch.
export const TOKEN_KEY = 'token';
export const USER_KEY = 'user';
const LS_TOKEN_KEY = TOKEN_KEY;
const LS_USER_KEY = USER_KEY;

export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/** `{ Authorization: Bearer ... }`, or `{}` when logged out — spread it into headers. */
export function getAuthHeader(): Record<string, string> {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Clear common auth keys too (in case some other code stored under different names)
const EXTRA_AUTH_KEYS = [
  'auth',
  'authToken',
  'accessToken',
  'jwt',
  'jwtToken',
  'refreshToken',
  'userData',
  'persist:root',
];

/**
 * Remove every auth key. Each removal is attempted on its own — one failure
 * must not leave the others in place — and the failures are RETURNED, not
 * swallowed, so a caller that is about to redirect "as logged out" can tell
 * whether a credential may still be sitting in storage.
 */
export function clearAuthStorage(): { failed: string[] } {
  const failed: string[] = [];
  const attempt = (label: string, fn: () => void) => {
    try {
      fn();
    } catch {
      failed.push(label);
    }
  };
  attempt(LS_TOKEN_KEY, () => localStorage.removeItem(LS_TOKEN_KEY));
  attempt(LS_USER_KEY, () => localStorage.removeItem(LS_USER_KEY));
  for (const k of EXTRA_AUTH_KEYS) attempt(k, () => localStorage.removeItem(k));
  attempt('sessionStorage', () => sessionStorage.clear());
  return { failed };
}

export function clearAuthAndRedirectToLogin() {
  clearAuthStorage();

  try {
    if (window.location.pathname !== '/login') {
      window.location.replace('/login');
    }
  } catch {
    // ignore
  }
}

